/**
 * Automatische Arbeitspaket-Nummerierung mit Projekt-Kürzel und Hierarchie.
 *
 * Schema (Kundenanforderung T-064):
 *   Top-Ebene:  <Kürzel>-AP-<N>        z. B. "TJ-AP-1", "TJ-AP-2"
 *   Unter-AP:   <Eltern-Nr>.<M>        z. B. "TJ-AP-1.1", "TJ-AP-1.1.1"
 * Das Kürzel kommt aus dem Projekt-Spitznamen (nickname); die Hierarchie aus
 * parent_id. Beliebig tief.
 */

/** Minimaler AP-Eintrag für die Nummern-Berechnung. */
export interface ApNumberNode {
  id: string;
  nr: string;
  parent_id: string | null;
}

/**
 * Kürzel für die AP-Nummerierung: bevorzugt der Projekt-Spitzname (nickname),
 * sonst die Initialen des Projektnamens, als letzter Fallback 'AP'.
 */
export function projectKuerzel(nickname: string | null | undefined, name?: string | null): string {
  const nn = nickname?.trim();
  if (nn) return nn;
  const initials = (name ?? '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 4);
  return initials || 'AP';
}

/** Laufende Nummer der untersten Ebene einer AP-Nr (letztes Segment nach '.'). */
export function lastSegment(nr: string): number {
  const seg = (nr ?? '').split('.').pop() ?? '';
  const m = seg.match(/(\d+)\s*$/);
  return m ? parseInt(m[1]!, 10) : 0;
}

/** Nächste freie Top-Level-Nr: `<Kürzel>-AP-<N>`. */
export function nextTopLevelNr(kuerzel: string, all: ApNumberNode[]): string {
  const max = all
    .filter((a) => !a.parent_id)
    .reduce((m, a) => Math.max(m, lastSegment(a.nr)), 0);
  return `${kuerzel}-AP-${max + 1}`;
}

/** Nächste freie Unter-AP-Nr unter einem Eltern-AP: `<Eltern-Nr>.<M>`. */
export function nextChildNr(parent: ApNumberNode, all: ApNumberNode[]): string {
  const max = all
    .filter((a) => a.parent_id === parent.id)
    .reduce((m, a) => Math.max(m, lastSegment(a.nr)), 0);
  return `${parent.nr}.${max + 1}`;
}

/** WBS-Zahlenpfad aus einer AP-Nr: nimmt den Teil nach '-AP-' (falls vorhanden),
 *  sonst die ganze Nr, und zerlegt ihn an '.' in Zahlen. "1.2" → [1,2];
 *  "TJ-AP-1.2" → [1,2]; nicht-numerische Nr → []. */
export function wbsPath(nr: string): number[] {
  const tail = nr.includes('-AP-') ? (nr.split('-AP-').pop() ?? '') : nr;
  const parts = tail.split('.');
  const nums = parts.map((s) => parseInt(s, 10));
  return nums.every((n) => Number.isFinite(n)) && nums.length > 0 ? nums : [];
}

/** Geplante Umnummerierung eines AP aufs Kürzel-Schema. */
export interface RenumberPlan { id: string; nr: string; parent_id: string | null; }

/**
 * Stellt bestehende APs aufs Schema `<Kürzel>-AP-<WBS>` um und leitet die
 * parent_id-Hierarchie aus den (heute impliziten) WBS-Nummern ab: "1.1" wird Kind
 * von "1". Liefert nur Einträge, bei denen sich Nr. oder parent_id tatsächlich
 * ändert. APs ohne erkennbaren WBS-Pfad bleiben unangetastet.
 */
export function computeRenumbering(items: ApNumberNode[], kuerzel: string): RenumberPlan[] {
  const withPath = items
    .map((it) => ({ it, path: wbsPath(it.nr) }))
    .filter((x) => x.path.length > 0);
  const idByWbs = new Map<string, string>();
  for (const { it, path } of withPath) idByWbs.set(path.join('.'), it.id);

  const plan: RenumberPlan[] = [];
  for (const { it, path } of withPath) {
    const newNr = `${kuerzel}-AP-${path.join('.')}`;
    const parentPath = path.slice(0, -1);
    const newParentId = parentPath.length > 0 ? (idByWbs.get(parentPath.join('.')) ?? null) : null;
    if (it.nr !== newNr || it.parent_id !== newParentId) {
      plan.push({ id: it.id, nr: newNr, parent_id: newParentId });
    }
  }
  return plan;
}

/** Set der IDs, die mindestens ein Kind haben (für das Auf-/Zuklapp-Steuerelement). */
export function apParentIds(items: ApNumberNode[]): Set<string> {
  const s = new Set<string>();
  for (const it of items) if (it.parent_id) s.add(it.parent_id);
  return s;
}

/** IDs, die unter einem zugeklappten Vorfahren liegen und daher auszublenden sind. */
export function collapsedHiddenIds(items: ApNumberNode[], collapsed: Set<string>): Set<string> {
  const hidden = new Set<string>();
  if (collapsed.size === 0) return hidden;
  const byId = new Map(items.map((i) => [i.id, i]));
  for (const it of items) {
    const seen = new Set<string>();
    let cur = it.parent_id ? byId.get(it.parent_id) : undefined;
    while (cur && !seen.has(cur.id)) {
      seen.add(cur.id);
      if (collapsed.has(cur.id)) { hidden.add(it.id); break; }
      cur = cur.parent_id ? byId.get(cur.parent_id) : undefined;
    }
  }
  return hidden;
}

/** Alle Nachkommen-IDs eines AP (rekursiv über parent_id). */
export function descendantIds(items: ApNumberNode[], id: string): Set<string> {
  const childrenOf = new Map<string, string[]>();
  for (const it of items) {
    if (!it.parent_id) continue;
    const arr = childrenOf.get(it.parent_id) ?? [];
    arr.push(it.id);
    childrenOf.set(it.parent_id, arr);
  }
  const out = new Set<string>();
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop()!;
    for (const c of childrenOf.get(cur) ?? []) {
      if (!out.has(c)) { out.add(c); stack.push(c); }
    }
  }
  return out;
}

/** True, wenn das Umhängen von childId unter newParentId einen Zyklus erzeugen würde
 *  (newParent ist das AP selbst oder einer seiner Nachkommen). */
export function wouldCreateApCycle(items: ApNumberNode[], childId: string, newParentId: string | null): boolean {
  if (!newParentId) return false;
  if (newParentId === childId) return true;
  return descendantIds(items, childId).has(newParentId);
}

/**
 * Plan für das Umhängen von childId unter newParentId (null = oberste Ebene):
 * setzt die neue parent_id und nummeriert das AP samt aller Nachkommen aufs neue
 * WBS-Schema um (`<neue-Basis>.<...>`). Liefert [] bei Zyklus oder Nicht-Änderung.
 */
export function reparentPlan(items: ApNumberNode[], childId: string, newParentId: string | null, kuerzel: string): RenumberPlan[] {
  const child = items.find((i) => i.id === childId);
  if (!child) return [];
  if ((child.parent_id ?? null) === (newParentId ?? null)) return [];
  if (wouldCreateApCycle(items, childId, newParentId)) return [];

  const newParent = newParentId ? items.find((i) => i.id === newParentId) ?? null : null;
  const newBaseNr = newParent
    ? nextChildNr({ id: newParent.id, nr: newParent.nr, parent_id: newParent.parent_id }, items)
    : nextTopLevelNr(kuerzel, items);
  const oldBaseNr = child.nr;

  const subtree = new Set<string>([childId, ...descendantIds(items, childId)]);
  const plan: RenumberPlan[] = [];
  for (const it of items) {
    if (!subtree.has(it.id)) continue;
    const newNr = it.id === childId
      ? newBaseNr
      : it.nr.startsWith(`${oldBaseNr}.`)
        ? `${newBaseNr}${it.nr.slice(oldBaseNr.length)}`
        : it.nr;
    const newPid = it.id === childId ? (newParentId ?? null) : it.parent_id;
    if (it.nr !== newNr || (it.parent_id ?? null) !== newPid) {
      plan.push({ id: it.id, nr: newNr, parent_id: newPid });
    }
  }
  return plan;
}

/** Tiefe (0 = Wurzel) eines AP in der parent_id-Kette. Robust gegen Zyklen (Cap). */
export function apDepth(node: ApNumberNode, byId: Map<string, ApNumberNode>): number {
  let depth = 0;
  let cur = node;
  const seen = new Set<string>();
  while (cur.parent_id && !seen.has(cur.id)) {
    seen.add(cur.id);
    const parent = byId.get(cur.parent_id);
    if (!parent) break;
    depth += 1;
    cur = parent;
    if (depth > 50) break;
  }
  return depth;
}

/**
 * Bringt eine flache AP-Liste in hierarchische Reihenfolge (Eltern vor Kindern,
 * Geschwister nach laufender Nummer) und liefert je AP die Tiefe für die Einrückung.
 * Verwaiste (parent nicht in der Liste) werden als Wurzeln behandelt.
 */
export function buildApTree<T extends ApNumberNode>(items: T[]): Array<{ node: T; depth: number }> {
  const byId = new Map<string, T>(items.map((i) => [i.id, i]));
  const childrenOf = new Map<string | null, T[]>();
  for (const it of items) {
    const key = it.parent_id && byId.has(it.parent_id) ? it.parent_id : null;
    const arr = childrenOf.get(key) ?? [];
    arr.push(it);
    childrenOf.set(key, arr);
  }
  const sortSiblings = (a: T, b: T) => lastSegment(a.nr) - lastSegment(b.nr) || a.nr.localeCompare(b.nr);
  const out: Array<{ node: T; depth: number }> = [];
  const walk = (parentKey: string | null, depth: number) => {
    const kids = (childrenOf.get(parentKey) ?? []).slice().sort(sortSiblings);
    for (const k of kids) {
      out.push({ node: k, depth });
      walk(k.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}
